import { useContext, useMemo } from "react";
import {
  get,
  find,
  isEqual,
  uniqBy,
  differenceBy,
  cloneDeep,
  size,
  times,
} from "lodash";
import cx from "classnames";
import moment from "moment";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";

import { DealRoomContext } from "../../../context";
import { DealRoomActionPlanContext } from "../../context";

import Text from "src/components/text";
import AssigneesPopover from "./popovers/recipients";
import AssetsPopover from "./popovers/assets";
import DueDatePopover from "./popovers/dueDate";

import {
  DealRoomAction,
  DealRoomActionEditChanges,
  DealRoomRecipient,
} from "src/types/dealRoom";
import { Asset } from "src/types/assets";

import styles from "./styles.module.scss";

const hasDueDateChanged = (
  newDueDate?: string | null,
  oldDueDate?: string | null,
) => {
  if (!newDueDate && !oldDueDate) {
    return false;
  }

  if (!newDueDate || !oldDueDate) {
    return true;
  }

  return !moment(newDueDate).isSame(moment(oldDueDate));
};

const StageAction = ({ action, editing, isNewAction, dragging, saving }) => {
  const {
    state,
    toggleEditAction,
    handleCancelEditAction,
    handleSaveAction,
    handleSelectRecipient,
    clickCompleteAction,
    clickSaveAction,
    clickDeleteAction,
    handleEditAction,
  } = useContext(DealRoomActionPlanContext);

  const editMode = get(state, "editMode");
  const editingItem = get(state, "editingItem");

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: action.id,
      disabled: !editMode || !!editingItem,
      data: {
        action: action,
      },
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const { id: dealRoomId, dealRoom } = useContext(DealRoomContext);

  const {
    id,
    actionItem,
    dueDate,
    assets,
    recipients,
    stage,
    completed,
    canEdit,
  } = action;

  const shouldWiggle =
    get(editingItem, "type") === "action" && get(editingItem, "id") === id;
  const wiggleKey = get(editingItem, "wiggleKey", 0);

  const currentUser = useSelector((state) => get(state, "users.me"));

  const canEditAction = useMemo(() => {
    const currentUserId = get(currentUser, "id", "");
    const dealRoomOwnerId = get(dealRoom, "createdBy.id", "");

    const hasDealRoomEditPermission = get(dealRoom, "canEdit", false);
    const isDealRoomOwner =
      currentUserId && dealRoomOwnerId && currentUserId === dealRoomOwnerId;

    return isDealRoomOwner || hasDealRoomEditPermission || canEdit;
  }, [dealRoom, currentUser]);

  const handleToggleEditAction = () => {
    if (!canEditAction || !editMode) {
      return;
    }

    toggleEditAction(stage, id, completed);
  };

  const getActionChanges = (stageId: string, action: DealRoomAction) => {
    const changes: DealRoomActionEditChanges = {};
    let stakeholdersToBeNotified: DealRoomRecipient[] = [];

    const newDueDate = get(action, "dueDate", "");
    const newRecipients = get(action, "recipients", []);
    const newAssets = get(action, "assets", []);
    const newActionItem = get(action, "actionItem", "");

    const actions = get(state, `actions[${stageId}].items`, []);
    const oldAction = find(actions, (action) => action.id === id);

    if (!oldAction) {
      return { changes, stakeholdersToBeNotified: newRecipients };
    }

    const oldRecipients = get(
      oldAction,
      "recipients",
      [],
    ) as DealRoomRecipient[];
    const oldAssets = get(oldAction, "assets", []) as Asset[];
    const oldDueDate = get(oldAction, "dueDate", "");
    const oldActionItem = get(oldAction, "actionItem", "");

    if (!isEqual(newActionItem, oldActionItem)) {
      changes.actionItem = newActionItem ?? "";
    }
    if (!isEqual(newRecipients, oldRecipients)) {
      changes.recipients = newRecipients ?? [];

      const newlyAddedRecipients = differenceBy(newRecipients, oldRecipients, 'shareRecipient');
      const removeRecipients = differenceBy(oldRecipients, newRecipients, 'shareRecipient');

      stakeholdersToBeNotified = [...stakeholdersToBeNotified, ...newlyAddedRecipients, ...removeRecipients];
    }
    if (!isEqual(newAssets, oldAssets)) {
      changes.assets = newAssets ?? [];

      stakeholdersToBeNotified = [...stakeholdersToBeNotified, ...oldRecipients];
    }
    if (hasDueDateChanged(newDueDate, oldDueDate)) {
      changes.dueDate = newDueDate ?? "";

      stakeholdersToBeNotified = [...stakeholdersToBeNotified, ...oldRecipients];
    }
    
    const result = { changes, stakeholdersToBeNotified: uniqBy(stakeholdersToBeNotified, "shareRecipient") };
    return result;
  };

  const handleActionItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedActionItem = e.target.value;
    handleEditAction({ actionItem: updatedActionItem }, stage, id, isNewAction);
  };

  const handleSaveClick = () => {
    if (saving || size(get(action, "actionItem", "").trim()) === 0) {
      return;
    }
    
    const { changes, stakeholdersToBeNotified } = getActionChanges(stage, action) ?? {};

    const { actionItem, ...changesForSlider } = cloneDeep(changes);
    const hasSliderChanges = size(changesForSlider) > 0;
    const hasActionItemChange = Object.hasOwn(changes, "actionItem");

    if (hasActionItemChange || isNewAction) {
      handleSaveAction(action, stage, isNewAction);
      return;
    }

    if (hasSliderChanges) {
      clickSaveAction(action, stage, changesForSlider, stakeholdersToBeNotified);
      return;
    }

    handleCancelEditAction(action, isNewAction);
  };

  return (
    <div
      className={cx(styles.action, {
        [styles.wiggle]: shouldWiggle && wiggleKey,
        [styles.dragging]: dragging,
      })}
      style={style}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      key={shouldWiggle ? `action-${wiggleKey}` : `action-${id}`}
    >
      <div className={styles.leftGroup}>
        {editMode && (
          <i
            className={cx(styles.drag, "paperflite-drag", {
              [styles.disabled]: isNewAction && editing,
            })}
          />
        )}
        {!editMode && <i
          className={cx(styles.checkButton, {
            "paperflite-radio-button-unselected": !completed,
            "fa-solid fa-check-circle": completed,
            [styles.checked]: completed,
            [styles.disabled]: editMode || isNewAction || !canEditAction,
          })}
          onClick={() => {
            if (editMode || isNewAction || !canEditAction) {
              return;
            }

            clickCompleteAction(stage, id, !completed);
          }}
        />}
        {editMode && editing ? (
          <input
            value={actionItem}
            className={styles.titleInput}
            onChange={handleActionItemChange}
            onKeyDown={(e) =>{
              if(e.key === "Enter"){
                handleSaveClick();
              }
            }}
            placeholder="Add an action item"
            id={`action-${id}`}
            name={`action-${id}`}
            autoFocus
          />
        ) : (
          <p
            className={cx(styles.title, {
              [styles.active]: completed,
              [styles.disabled]: !canEditAction,
            })}
            onClick={handleToggleEditAction}
          >
            <Text className={styles.text} text={!shouldWiggle && isNewAction ? "Add an action item" : actionItem} lines={1} />
          </p>
        )}
      </div>

      <div
        className={cx(styles.rightGroup, {
          [styles.showRightGroup]: true,
          [styles.existingAction]: !isNewAction,
          [styles.showSaveGroup]: editMode && editing,
          [styles.showDeleteGroup]: !isNewAction && editMode && !editing,
          [styles.showDeleteIcon]: !isNewAction && editMode && !editing && !!editingItem,
        })}
      >
        {(size(recipients) > 0 || editMode) ? (
          <AssigneesPopover
            dealRoomId={dealRoomId || ""}
            stageId={stage}
            actionId={id}
            selectedRecipients={recipients}
            onSelectRecipient={handleSelectRecipient}
            isNewAction={isNewAction}
            onClick={handleToggleEditAction}
            canEditAction={canEditAction}
            isActionCompleted={completed}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-add-assignee")} />
            <p className={styles.buttonText}>No assignees</p>
          </div>
        )}

        {(dueDate || editMode) ? (
          <DueDatePopover
            dealRoomId={dealRoomId || ""}
            stageId={stage}
            actionId={id}
            isNewAction={isNewAction}
            dueDate={dueDate}
            onClick={handleToggleEditAction}
            disabled={!canEditAction || !editMode}
            canEditAction={canEditAction}
            isActionCompleted={completed}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-calendar")} />
            <p className={styles.buttonText}>No due date</p>
          </div>
        )}

        {(size(assets) > 0 || editMode) ? (
          <AssetsPopover
            stageId={stage}
            actionId={id}
            isNewAction={isNewAction}
            onClick={handleToggleEditAction}
            selectedAssets={assets}
            canEditAction={canEditAction}
            isActionCompleted={completed}
          />
        ) : (
          <div className={cx(styles.actionButton, styles.disabled)}>
            <i className={cx(styles.icon, "paperflite-attachment")} />
            <p className={styles.buttonText}>0 attachments</p>
          </div>
        )}

        {!isNewAction && editMode && !editing && (
          <div className={styles.deleteGroup}>
            <i
              className={cx(styles.icon, "paperflite-delete")}
              onClick={() => clickDeleteAction(action, stage)}
            />
          </div>
        )}

        {editMode && (editing) && (
          <div className={styles.saveGroup}>
            <i
              className={cx(styles.icon, styles.save, "paperflite-tick", {
                [styles.loading]: saving || size(actionItem.trim()) === 0,
              })}
              onClick={handleSaveClick}
            />
            <div className={styles.separator}></div>
            <i
              className={cx(styles.icon, "paperflite-close", {
                [styles.loading]: saving,
              })}
              onClick={() => {
                if (saving) {
                  return;
                }

                handleCancelEditAction(action, isNewAction);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StageAction;

export const StageActionLoadingSkeleton = () => {
  return (
    <>
      {times(3, () => (
        <div className={styles.action}>
          <div className={styles.leftGroup}>
            <div className={styles.placeholder}></div>
          </div>
          <div className={styles.rightGroup}>
            {times(3, () => (
              <div className={styles.placeholder}></div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};
